import type { UserDataLoginRecord } from '@navio/nsdk';
import { ErrorMessage, NoddiButton } from 'noddi-ui-web';
import { useState } from 'react';
import type { UserSessionData } from '../../contexts/AuthContext';
import { EmailLogin } from '../Login/EmailLogin';

type MatchTranslations = {
  createNewAccount: string;
  emailAlreadyHasAccount: string;
  emailSignInLinkPrompt: string;
  sendSignInLink: string;
  signInLinkSent: string;
  email: string;
  password: string;
  signIn: string;
  forgotPassword: string;
  passwordResetSuccess: (email: string) => string;
  accessDeniedLoginMessage: string;
};

type Props = {
  email: string;
  /** Exactly one account holds this email, so a link can be mailed to it. */
  emailLoginAvailable: boolean;
  /**
   * Mail a single-use sign-in link to the matched address. The signup challenge proves
   * the caller owns the phone number and nothing else, so the account is reached by
   * opening the link, never from here.
   */
  onSendSignInLink: () => void;
  sendingSignInLink: boolean;
  signInLinkSent: boolean;
  /** Create a new user instead of using the matched account. */
  onCreateNewAccount: () => void;
  creatingNewAccount: boolean;
  onLoginSuccess?: (userData: UserSessionData) => void;
  trackUserIdentity?: (userData: UserDataLoginRecord) => void;
  translations: MatchTranslations;
};

const SignupEmailLogin = ({
  email,
  onLoginSuccess,
  trackUserIdentity,
  translations
}: {
  email: string;
  onLoginSuccess?: (userData: UserSessionData) => void;
  trackUserIdentity?: (userData: UserDataLoginRecord) => void;
  translations: MatchTranslations;
}) => {
  const [emailLoginError, setEmailLoginError] = useState('');

  return (
    <>
      <p>{translations.emailAlreadyHasAccount}</p>
      <EmailLogin
        key={email}
        initialEmail={email}
        enablePasswordReset
        setErrorMessage={setEmailLoginError}
        onLoginSuccess={onLoginSuccess}
        trackUserIdentity={trackUserIdentity}
        translations={{
          emailAddress: translations.email,
          password: translations.password,
          logIn: translations.signIn,
          forgotPassword: translations.forgotPassword,
          passwordResetSuccess: translations.passwordResetSuccess,
          accessDeniedLoginMessage: translations.accessDeniedLoginMessage
        }}
      />
      {emailLoginError && <ErrorMessage message={emailLoginError} />}
    </>
  );
};

export const SignupAccountMatch = ({
  email,
  emailLoginAvailable,
  onSendSignInLink,
  sendingSignInLink,
  signInLinkSent,
  onCreateNewAccount,
  creatingNewAccount,
  onLoginSuccess,
  trackUserIdentity,
  translations
}: Props) => (
  <div className='mt-4 flex flex-col gap-3'>
    {emailLoginAvailable &&
      (signInLinkSent ? (
        <p>{translations.signInLinkSent}</p>
      ) : (
        <>
          <p>{translations.emailSignInLinkPrompt}</p>
          <NoddiButton type='button' className='w-full' loading={sendingSignInLink} onClick={onSendSignInLink}>
            {translations.sendSignInLink}
          </NoddiButton>
        </>
      ))}
    <SignupEmailLogin
      email={email}
      onLoginSuccess={onLoginSuccess}
      trackUserIdentity={trackUserIdentity}
      translations={translations}
    />
    <NoddiButton
      type='button'
      variant='outline'
      className='w-full'
      loading={creatingNewAccount}
      disabled={creatingNewAccount}
      onClick={onCreateNewAccount}
    >
      {translations.createNewAccount}
    </NoddiButton>
  </div>
);
